import Head from 'next/head';
import Link from 'next/link';
import { useState } from 'react';
import { useRouter } from 'next/router';
import {
  Bus, Search, Users, Calculator, Star, Shield, Clock, Headphones,
  CheckCircle, ArrowRight, MapPin, ChevronRight, Phone, Mail,
  Wifi, Wind, Tv, Coffee, Leaf, Award, TrendingUp
} from 'lucide-react';
import Header from '../components/Header';

const TRUST_STATS = [
  { icon: TrendingUp, value: '1,200+', label: 'Trips Completed' },
  { icon: Users, value: '15,000+', label: 'Happy Passengers' },
  { icon: Award, value: '4.8 / 5', label: 'Average Rating' },
  { icon: Shield, value: '100%', label: 'Insured Vehicles' },
];

const VEHICLE_CATEGORIES = [
  {
    type: 'bus',
    label: 'Buses',
    emoji: '🚌',
    tagline: 'Ideal for large groups & events',
    seats: '30–50 seats',
    features: ['AC', 'Reclining Seats', 'Large Luggage Bay'],
    color: 'from-green-500 to-emerald-600',
    bg: 'bg-green-50',
    badge: 'Most Popular',
  },
  {
    type: 'minibus',
    label: 'Minibuses',
    emoji: '🚐',
    tagline: 'Perfect for medium-sized groups',
    seats: '12–25 seats',
    features: ['AC', 'Comfortable Seats', 'USB Charging'],
    color: 'from-blue-500 to-indigo-600',
    bg: 'bg-blue-50',
    badge: 'Great Value',
  },
  {
    type: 'car',
    label: 'Cars & SUVs',
    emoji: '🚗',
    tagline: 'Great for small groups & families',
    seats: '4–8 seats',
    features: ['AC', 'Premium Comfort', 'Flexible Routes'],
    color: 'from-purple-500 to-violet-600',
    bg: 'bg-purple-50',
    badge: 'Premium',
  },
];

const TESTIMONIALS = [
  {
    name: 'Priya Sharma',
    role: 'Event Organiser',
    text: 'Booked a 45-seater bus for our company offsite. The driver was punctual and the vehicle was immaculate. Will definitely use again!',
    rating: 5,
    avatar: 'PS',
    color: 'bg-green-500',
  },
  {
    name: 'Arjun Nair',
    role: 'Tour Group Leader',
    text: 'Used BlisssBus for a Goa trip with 40 people. The AC coach was comfortable and the booking process was seamless. Highly recommended.',
    rating: 5,
    avatar: 'AN',
    color: 'bg-blue-500',
  },
  {
    name: 'Meera Pillai',
    role: 'School Principal',
    text: 'Excellent service for our annual school trip. Safety was clearly a priority and the bus arrived 15 minutes early. Very impressed.',
    rating: 5,
    avatar: 'MP',
    color: 'bg-purple-500',
  },
];

const OCCASIONS = [
  {
    emoji: '🏢',
    title: 'Corporate Events',
    desc: 'Team offsites, airport transfers, client shuttles, and conference travel.',
    tags: ['AC Coach', 'Wi-Fi', 'Punctual'],
    link: '/search-results?vehicleType=bus',
    linkLabel: 'Browse Corporate Buses',
    gradient: 'from-blue-50 to-indigo-100',
    accent: 'bg-blue-600',
  },
  {
    emoji: '🎓',
    title: 'School & College Trips',
    desc: 'Safe, teacher-supervised travel with first-aid ready drivers.',
    tags: ['Safety First', 'GPS Tracked', 'Insured'],
    link: '/search-results?vehicleType=bus',
    linkLabel: 'View School Buses',
    gradient: 'from-green-50 to-emerald-100',
    accent: 'bg-green-600',
  },
  {
    emoji: '💒',
    title: 'Weddings & Functions',
    desc: 'Guest shuttles, baraat buses, and venue runs for your big day.',
    tags: ['Decorated Options', 'Flexible Routes', 'Large Fleet'],
    link: '/get-quote',
    linkLabel: 'Get a Wedding Quote',
    gradient: 'from-pink-50 to-rose-100',
    accent: 'bg-rose-600',
  },
  {
    emoji: '🛕',
    title: 'Pilgrimages & Tours',
    desc: 'Comfortable multi-day coaches for religious routes and temple tours.',
    tags: ['Long Distance', 'Reclining Seats', 'Restroom'],
    link: '/search-results?vehicleType=bus',
    linkLabel: 'Book a Tour Bus',
    gradient: 'from-orange-50 to-amber-100',
    accent: 'bg-orange-600',
  },
  {
    emoji: '🎉',
    title: 'Group Outings',
    desc: "Picnics, weekend getaways, and group parties \u2014 we'll get everyone there.",
    tags: ['Flexible Timing', 'Any Size', 'Affordable'],
    link: '/search-results',
    linkLabel: 'Browse All Vehicles',
    gradient: 'from-purple-50 to-violet-100',
    accent: 'bg-purple-600',
  },
  {
    emoji: '✈️',
    title: 'Airport Transfers',
    desc: 'On-time pickups and drop-offs for individuals and groups of any size.',
    tags: ['Flight Tracking', 'Meet & Greet', 'Any Time'],
    link: '/search-results?vehicleType=car',
    linkLabel: 'Book a Transfer',
    gradient: 'from-sky-50 to-cyan-100',
    accent: 'bg-sky-600',
  },
];

const STAY_OCCASIONS = [
  {
    emoji: '🏨',
    title: 'Luxury Hotels',
    desc: 'Premium accommodations with top-tier amenities for a perfect getaway.',
    tags: ['Pool', 'Spa', 'Fine Dining'],
    link: '/hotels',
    linkLabel: 'Browse Luxury Hotels',
    gradient: 'from-amber-50 to-orange-100',
    accent: 'bg-amber-600',
  },
  {
    emoji: '🏡',
    title: 'Cozy Homestays',
    desc: 'Experience local culture and authentic hospitality in beautiful settings.',
    tags: ['Local Food', 'Nature', 'Quiet'],
    link: '/hotels',
    linkLabel: 'View Homestays',
    gradient: 'from-green-50 to-emerald-100',
    accent: 'bg-green-600',
  },
  {
    emoji: '🏖️',
    title: 'Resorts & Retreats',
    desc: 'All-inclusive resorts for family vacations and corporate offsites.',
    tags: ['Beachfront', 'Activities', 'Relaxing'],
    link: '/hotels',
    linkLabel: 'Explore Resorts',
    gradient: 'from-blue-50 to-cyan-100',
    accent: 'bg-blue-600',
  }
];

export default function Home() {
  const router = useRouter();
  const [activeTab, setActiveTab] = useState('transport');
  const [vehicleType, setVehicleType] = useState('');
  const [passengers, setPassengers] = useState('');
  const [transportStart, setTransportStart] = useState('');
  const [transportReturn, setTransportReturn] = useState('');
  
  // Stays Search State
  const [hotelLocation, setHotelLocation] = useState('');
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [guests, setGuests] = useState('2');

  const handleSearch = () => {
    if (activeTab === 'transport') {
      const params = new URLSearchParams();
      if (vehicleType) params.append('vehicleType', vehicleType);
      if (transportStart) params.append('startDate', transportStart);
      if (transportReturn) params.append('returnDate', transportReturn);
      if (passengers) params.append('passengers', passengers);
      
      const queryString = params.toString();
      router.push(`/search-results${queryString ? `?${queryString}` : ''}`);
    } else {
      const params = new URLSearchParams({
        location: hotelLocation,
        checkIn,
        checkOut,
        guests
      });
      router.push(`/hotels?${params.toString()}`);
    }
  };

  return (
    <div className="min-h-screen bg-white font-sans">
      <Head>
        <title>BlisssBus – Book Buses, Minibuses & Cars Online</title>
        <meta name="description" content="Book buses, minibuses, and cars for groups, tours, and corporate travel across India. Easy online booking, transparent pricing." />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap" rel="stylesheet" />
      </Head>

      {/* ===== STICKY HEADER ===== */}
      <Header />

      {/* ===== HERO SECTION ===== */}
      <section className="hero-gradient text-white relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0">
          <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-emerald-400/20 blur-[120px]"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] rounded-full bg-blue-400/20 blur-[150px]"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full opacity-[0.03] pointer-events-none bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-12">
          {/* Hero text */}
          <div className="text-center max-w-4xl mx-auto mb-12">
            <div className="inline-flex items-center gap-2 glass rounded-full px-4 py-2 mb-8 text-sm font-bold text-green-100 animate-bounce-subtle">
              <Star className="h-4 w-4 text-emerald-300" /> Rated 4.8/5 by 15,000+ happy travelers
            </div>
            <h1 className="text-5xl md:text-7xl font-black leading-tight mb-6 tracking-tight">
              Your Journey,<br />
              <span className="text-emerald-300">Perfectly Planned.</span>
            </h1>
            <p className="text-xl text-green-100 leading-relaxed font-medium max-w-2xl mx-auto">
              Premium buses, cozy tourist homes, and seamless car rentals. 
              The most trusted travel partner for groups and families.
            </p>
          </div>

          {/* ===== INTEGRATED QUICK SEARCH ===== */}
          <div className="max-w-4xl mx-auto">
            <div className="glass rounded-2xl p-2 md:p-3 relative overflow-visible">
              <div className="bg-white rounded-xl shadow-2xl overflow-hidden">
                {/* Tabs */}
                <div className="flex border-b border-gray-100 bg-gray-50/50">
                  <button 
                    onClick={() => setActiveTab('transport')}
                    className={`flex-1 flex items-center justify-center gap-2 py-4 text-sm font-bold transition-all ${activeTab === 'transport' ? 'bg-white text-green-600 border-b-2 border-green-600' : 'text-gray-400 hover:text-gray-600'}`}
                  >
                    <Bus className="h-4 w-4" /> Transport
                  </button>
                  <button 
                    onClick={() => setActiveTab('stays')}
                    className={`flex-1 flex items-center justify-center gap-2 py-4 text-sm font-bold transition-all ${activeTab === 'stays' ? 'bg-white text-green-600 border-b-2 border-green-600' : 'text-gray-400 hover:text-gray-600'}`}
                  >
                    <MapPin className="h-4 w-4" /> Stays
                  </button>
                </div>

                <div className="p-6 md:p-8">
                  {activeTab === 'transport' ? (
                    <>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">Vehicle Type</label>
                        <select
                          value={vehicleType}
                          onChange={e => setVehicleType(e.target.value)}
                          className="w-full border border-gray-200 focus:border-green-500 rounded-xl px-4 py-3 text-sm font-medium text-gray-800 bg-gray-50 focus:bg-white transition-all outline-none"
                        >
                          <option value="">All Vehicles</option>
                          <option value="bus">🚌 Bus (30–50 seats)</option>
                          <option value="minibus">🚐 Minibus (12–25 seats)</option>
                          <option value="car">🚗 Car / SUV (4–8 seats)</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">Passengers</label>
                        <div className="relative">
                          <Users className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                          <input
                            type="number"
                            min="1"
                            placeholder="How many?"
                            value={passengers}
                            onChange={e => setPassengers(e.target.value)}
                            className="pl-10 w-full border border-gray-200 focus:border-green-500 rounded-xl px-4 py-3 text-sm font-medium text-gray-800 bg-gray-50 focus:bg-white transition-all outline-none"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                      <div>
                        <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">Start Date</label>
                        <input
                          type="date"
                          value={transportStart}
                          onChange={e => setTransportStart(e.target.value)}
                          className="w-full border border-gray-200 focus:border-green-500 rounded-xl px-4 py-3 text-sm font-medium text-gray-800 bg-gray-50 focus:bg-white transition-all outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">Return Date</label>
                        <input
                          type="date"
                          value={transportReturn}
                          onChange={e => setTransportReturn(e.target.value)}
                          className="w-full border border-gray-200 focus:border-green-500 rounded-xl px-4 py-3 text-sm font-medium text-gray-800 bg-gray-50 focus:bg-white transition-all outline-none"
                        />
                      </div>
                      <div className="flex items-end">
                        <button
                          onClick={handleSearch}
                          className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3.5 px-6 rounded-xl transition-all flex items-center justify-center gap-2 shadow-lg shadow-green-600/20"
                        >
                          <Search className="h-4 w-4" /> Search Transport
                        </button>
                      </div>
                    </div>
                    </>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                      <div className="md:col-span-1">
                        <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">Where to?</label>
                        <div className="relative">
                          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                          <input
                            type="text"
                            placeholder="Destination or Hotel"
                            value={hotelLocation}
                            onChange={e => setHotelLocation(e.target.value)}
                            className="pl-10 w-full border border-gray-200 focus:border-green-500 rounded-xl px-4 py-3 text-sm font-medium text-gray-800 bg-gray-50 focus:bg-white transition-all outline-none"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 md:col-span-2">
                        <div>
                          <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">Check-in</label>
                          <input
                            type="date"
                            value={checkIn}
                            onChange={e => setCheckIn(e.target.value)}
                            className="w-full border border-gray-200 focus:border-green-500 rounded-xl px-3 py-3 text-sm font-medium text-gray-800 bg-gray-50 focus:bg-white transition-all outline-none"
                          />
                        </div>
                        <div>
                          <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">Check-out</label>
                          <input
                            type="date"
                            value={checkOut}
                            onChange={e => setCheckOut(e.target.value)}
                            className="w-full border border-gray-200 focus:border-green-500 rounded-xl px-3 py-3 text-sm font-medium text-gray-800 bg-gray-50 focus:bg-white transition-all outline-none"
                          />
                        </div>
                      </div>
                      <div className="flex items-end">
                        <button
                          onClick={handleSearch}
                          className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3.5 px-6 rounded-xl transition-all flex items-center justify-center gap-2 shadow-lg shadow-green-600/20"
                        >
                          <Search className="h-4 w-4" /> Search Stays
                        </button>
                      </div>
                    </div>
                  )}
                  <div className="mt-6 flex items-center gap-6 text-[11px] font-bold text-gray-400 uppercase tracking-wider">
                    <span className="flex items-center gap-1.5"><CheckCircle className="h-3.5 w-3.5 text-green-500" /> Verified Listings</span>
                    <span className="flex items-center gap-1.5"><CheckCircle className="h-3.5 w-3.5 text-green-500" /> Lowest Price Guarantee</span>
                    <span className="flex items-center gap-1.5"><CheckCircle className="h-3.5 w-3.5 text-green-500" /> Instant Confirmation</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Trust Stats Bar */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-10 pb-4">
            {TRUST_STATS.map(({ icon: Icon, value, label }) => (
              <div key={label} className="stat-card rounded-xl p-4 text-center">
                <Icon className="h-5 w-5 text-emerald-300 mx-auto mb-2" />
                <div className="text-2xl font-extrabold">{value}</div>
                <div className="text-xs text-green-200 mt-0.5">{label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== WHO WE SERVE ===== */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-green-600 font-semibold text-sm uppercase tracking-widest">Who We Serve</span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mt-2">Transport for Every Occasion</h2>
            <p className="text-gray-500 mt-3 max-w-xl mx-auto">Whether it's a corporate event, a school trip, or a wedding — we have the right vehicle for you.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {OCCASIONS.map(({ emoji, title, desc, tags, link, linkLabel, gradient, accent }) => (
              <div key={title} className={`bg-gradient-to-br ${gradient} rounded-2xl p-6 border border-white/60 card-hover flex flex-col`}>
                <div className="text-4xl mb-3">{emoji}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed mb-4 flex-1">{desc}</p>
                <div className="flex flex-wrap gap-2 mb-5">
                  {tags.map(tag => (
                    <span key={tag} className="text-xs font-medium bg-white/70 text-gray-700 px-2.5 py-1 rounded-full border border-white">{tag}</span>
                  ))}
                </div>
                <Link
                  href={link}
                  className={`${accent} text-white text-sm font-semibold px-4 py-2.5 rounded-xl text-center hover:opacity-90 transition-opacity flex items-center justify-center gap-2`}
                >
                  {linkLabel} <ChevronRight className="h-4 w-4" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== STAYS FOR EVERY GETAWAY ===== */}
      <section className="py-16 bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-amber-600 font-semibold text-sm uppercase tracking-widest">Stays & Accommodations</span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mt-2">Stays for Every Getaway</h2>
            <p className="text-gray-500 mt-3 max-w-xl mx-auto">From premium luxury hotels to cozy local homestays — find your perfect home away from home.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {STAY_OCCASIONS.map(({ emoji, title, desc, tags, link, linkLabel, gradient, accent }) => (
              <div key={title} className={`bg-gradient-to-br ${gradient} rounded-2xl p-6 border border-gray-100 card-hover flex flex-col shadow-sm`}>
                <div className="text-4xl mb-3">{emoji}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed mb-4 flex-1">{desc}</p>
                <div className="flex flex-wrap gap-2 mb-5">
                  {tags.map(tag => (
                    <span key={tag} className="text-xs font-medium bg-white/70 text-gray-700 px-2.5 py-1 rounded-full border border-white">{tag}</span>
                  ))}
                </div>
                <Link
                  href={link}
                  className={`${accent} text-white text-sm font-semibold px-4 py-2.5 rounded-xl text-center hover:opacity-90 transition-opacity flex items-center justify-center gap-2`}
                >
                  {linkLabel} <ChevronRight className="h-4 w-4" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== VEHICLE CATEGORIES ===== */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-green-600 font-semibold text-sm uppercase tracking-widest">Our Fleet</span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mt-2">Choose Your Vehicle</h2>
            <p className="text-gray-500 mt-3 max-w-lg mx-auto">All vehicles are regularly inspected, insured, and operated by verified professional drivers.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {VEHICLE_CATEGORIES.map(cat => (
              <div key={cat.type} className={`relative rounded-2xl overflow-hidden border border-gray-100 card-hover ${cat.bg}`}>
                {cat.badge && (
                  <div className={`absolute top-4 right-4 bg-gradient-to-r ${cat.color} text-white text-xs font-bold px-3 py-1 rounded-full shadow-md`}>
                    {cat.badge}
                  </div>
                )}
                <div className="p-8">
                  <div className="text-5xl mb-4">{cat.emoji}</div>
                  <h3 className="text-2xl font-extrabold text-gray-900 mb-1">{cat.label}</h3>
                  <p className="text-sm text-gray-500 mb-1">{cat.tagline}</p>
                  <p className="text-xs font-semibold text-green-700 mb-5">{cat.seats}</p>

                  <ul className="space-y-2 mb-6">
                    {cat.features.map(f => (
                      <li key={f} className="flex items-center gap-2 text-sm text-gray-700">
                        <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>

                  <Link
                    href={`/search-results?vehicleType=${cat.type}`}
                    className={`flex items-center justify-center gap-2 w-full bg-gradient-to-r ${cat.color} text-white font-bold py-3 rounded-xl shadow-md hover:opacity-90 transition-opacity`}
                  >
                    View Available {cat.label}
                    <ChevronRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== QUOTE BANNER ===== */}
      <section className="py-14 bg-gradient-to-r from-indigo-600 to-blue-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div>
              <h2 className="text-3xl md:text-4xl font-extrabold mb-2">Not sure which vehicle?</h2>
              <p className="text-indigo-100 text-lg">Tell us your requirements and we'll send you the perfect custom quote — free, within 2 hours.</p>
            </div>
            <Link
              href="/get-quote"
              className="flex-shrink-0 bg-white text-indigo-700 hover:bg-indigo-50 font-bold px-8 py-4 rounded-xl shadow-xl transition-all btn-pulse flex items-center gap-3 text-lg"
            >
              <Calculator className="h-5 w-5" />
              Get My Free Quote
            </Link>
          </div>
        </div>
      </section>

      {/* ===== WHY CHOOSE US ===== */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-green-600 font-semibold text-sm uppercase tracking-widest">Why BlisssBus</span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mt-2">Travel with Confidence</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Shield, title: 'Fully Insured', desc: 'All vehicles carry comprehensive insurance for passenger safety', color: 'text-green-600', bg: 'bg-green-50' },
              { icon: Clock, title: 'Always On Time', desc: 'Punctuality is our commitment — drivers are tracked in real-time', color: 'text-blue-600', bg: 'bg-blue-50' },
              { icon: Headphones, title: '24/7 Support', desc: 'Round-the-clock customer support before, during, and after your trip', color: 'text-purple-600', bg: 'bg-purple-50' },
              { icon: Leaf, title: 'Eco-Friendly', desc: 'Fuel-efficient fleet reducing CO2 emissions for sustainable tourism', color: 'text-emerald-600', bg: 'bg-emerald-50' },
            ].map(({ icon: Icon, title, desc, color, bg }) => (
              <div key={title} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 card-hover">
                <div className={`inline-flex p-3 rounded-xl ${bg} mb-4`}>
                  <Icon className={`h-6 w-6 ${color}`} />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">{title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== TESTIMONIALS ===== */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-green-600 font-semibold text-sm uppercase tracking-widest">Testimonials</span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mt-2">What Our Customers Say</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {TESTIMONIALS.map(({ name, role, text, rating, avatar, color }) => (
              <div key={name} className="bg-gray-50 rounded-2xl p-6 border border-gray-100 card-hover">
                <div className="flex gap-1 mb-4">
                  {Array.from({ length: rating }).map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-700 leading-relaxed mb-5 text-sm">"{text}"</p>
                <div className="flex items-center gap-3">
                  <div className={`${color} w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0`}>
                    {avatar}
                  </div>
                  <div>
                    <div className="font-bold text-gray-900 text-sm">{name}</div>
                    <div className="text-gray-500 text-xs">{role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== ENVIRONMENT COMMITMENT ===== */}
      <section className="py-12 bg-gradient-to-r from-green-800 to-emerald-700 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <Leaf className="h-12 w-12 text-emerald-300 mx-auto mb-4" />
          <h2 className="text-3xl font-extrabold mb-3">Committed to Sustainable Tourism</h2>
          <p className="text-green-100 text-lg max-w-2xl mx-auto">
            By choosing shared group transport, your journey is already reducing per-person carbon emissions significantly compared to individual vehicles.
            Together, we've already saved <strong className="text-white">98,765 kg</strong> of CO₂.
          </p>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-10">
            {/* Brand */}
            <div className="md:col-span-1">
              <div className="flex items-center gap-2 mb-4">
                <div className="bg-green-600 p-2 rounded-lg">
                  <Bus className="h-5 w-5 text-white" />
                </div>
                <span className="text-xl font-bold">Blisss<span className="text-green-400">Bus</span></span>
              </div>
              <p className="text-gray-400 text-sm leading-relaxed">Sustainable group transport for tours, events, and corporate travel across India.</p>
            </div>

            {/* Browse */}
            <div>
              <h4 className="font-semibold mb-4 text-sm uppercase tracking-widest text-gray-400">Browse Vehicles</h4>
              <ul className="space-y-3 text-sm">
                <li><Link href="/search-results?vehicleType=bus" className="text-gray-300 hover:text-white transition-colors">🚌 Buses</Link></li>
                <li><Link href="/search-results?vehicleType=minibus" className="text-gray-300 hover:text-white transition-colors">🚐 Minibuses</Link></li>
                <li><Link href="/search-results?vehicleType=car" className="text-gray-300 hover:text-white transition-colors">🚗 Cars & SUVs</Link></li>
                <li><Link href="/search-results" className="text-gray-300 hover:text-white transition-colors">View All Vehicles</Link></li>
              </ul>
            </div>

            {/* Quick actions */}
            <div>
              <h4 className="font-semibold mb-4 text-sm uppercase tracking-widest text-gray-400">Quick Actions</h4>
              <ul className="space-y-3 text-sm">
                <li><Link href="/get-quote" className="text-gray-300 hover:text-white transition-colors">Get a Free Quote</Link></li>
                <li><Link href="/login" className="text-gray-300 hover:text-white transition-colors">Sign In</Link></li>
                <li><Link href="/register" className="text-gray-300 hover:text-white transition-colors">Create Account</Link></li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="font-semibold mb-4 text-sm uppercase tracking-widest text-gray-400">Contact Us</h4>
              <ul className="space-y-3 text-sm">
                <li className="flex items-center gap-2 text-gray-300"><Phone className="h-4 w-4 text-green-400" /> +91 94000 97300</li>
                <li className="flex items-center gap-2 text-gray-300"><Mail className="h-4 w-4 text-green-400" /> blissbusops@gmail.com</li>
                <li className="flex items-center gap-2 text-gray-300"><Clock className="h-4 w-4 text-green-400" /> Mon–Sun, 8am – 10pm</li>
              </ul>
            </div>
          </div>

          <hr className="section-divider border-gray-700 mb-6" />
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-gray-400">
            <p>&copy; {new Date().getFullYear()} BlisssBus. All rights reserved.</p>
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-green-500" />
              <span>SSL Secured · All bookings insured</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}